import { 
  doc, 
  getDoc, 
  setDoc, 
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { LayoutProfile, LayoutProfilesConfig, SidebarItem, SidebarFontConfig } from '../lib/types/sidebar';
import { UserRole } from '../lib/types/auth';
import { v4 as uuidv4 } from 'uuid';

const LAYOUT_PROFILES_DOC = 'settings/layout_profiles';

// Default layout profile (uses the main sidebar)
const defaultLayoutProfile: LayoutProfile = {
  id: 'default',
  name: 'Default Layout',
  roles: [], // Empty means all roles that don't match other profiles
  layoutType: 'sidebar',
  isDefault: true,
  order: 999,
  createdAt: new Date(),
  updatedAt: new Date(),
};

class LayoutProfileService {
  private cache: LayoutProfilesConfig | null = null;
  private unsubscribe: (() => void) | null = null;

  /**
   * Get all layout profiles
   */
  async getProfiles(): Promise<LayoutProfile[]> {
    try {
      const docRef = doc(db, LAYOUT_PROFILES_DOC);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as LayoutProfilesConfig;
        return data.profiles || [defaultLayoutProfile];
      }
      
      return [defaultLayoutProfile];
    } catch (error) {
      console.error('Error fetching layout profiles:', error);
      return [defaultLayoutProfile];
    }
  }

  /**
   * Subscribe to layout profiles changes
   */
  subscribeToProfiles(callback: (profiles: LayoutProfile[]) => void): () => void {
    const docRef = doc(db, LAYOUT_PROFILES_DOC);
    
    this.unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as LayoutProfilesConfig;
        this.cache = data;
        callback(data.profiles || [defaultLayoutProfile]);
      } else {
        callback([defaultLayoutProfile]);
      }
    }, (error) => {
      console.error('Error subscribing to layout profiles:', error);
      callback([defaultLayoutProfile]);
    });

    return () => {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    };
  }

  /**
   * Get the layout profile for a user based on their roles
   */
  async getProfileForUser(userRoles: UserRole[]): Promise<LayoutProfile> {
    const profiles = await this.getProfiles();
    return this.matchProfileToRoles(profiles, userRoles);
  }

  /**
   * Match a user's roles to the best layout profile
   */
  matchProfileToRoles(profiles: LayoutProfile[], userRoles: UserRole[]): LayoutProfile {
    if (!userRoles || userRoles.length === 0) {
      return profiles.find(p => p.isDefault) || defaultLayoutProfile;
    }

    // Sort profiles by order (lower = higher priority)
    const sortedProfiles = [...profiles].sort((a, b) => a.order - b.order);

    // Find the first profile that matches any of the user's roles
    for (const profile of sortedProfiles) {
      if (profile.roles.length === 0) continue; // Skip profiles with no specific roles
      
      const hasMatchingRole = userRoles.some(role => profile.roles.includes(role));
      if (hasMatchingRole) {
        return profile;
      }
    }

    // Fall back to default profile
    return profiles.find(p => p.isDefault) || defaultLayoutProfile;
  }

  /**
   * Save all layout profiles
   */
  async saveProfiles(profiles: LayoutProfile[]): Promise<void> {
    try {
      const docRef = doc(db, LAYOUT_PROFILES_DOC);
      await setDoc(docRef, {
        profiles,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error saving layout profiles:', error);
      throw error;
    }
  }

  /**
   * Create a new layout profile
   */
  async createProfile(profile: Omit<LayoutProfile, 'id' | 'createdAt' | 'updatedAt'>): Promise<LayoutProfile> {
    const profiles = await this.getProfiles();
    
    const newProfile: LayoutProfile = {
      ...profile,
      id: uuidv4(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await this.saveProfiles([...profiles, newProfile]);
    return newProfile;
  }

  /**
   * Update an existing layout profile
   */
  async updateProfile(profileId: string, updates: Partial<LayoutProfile>): Promise<void> {
    const profiles = await this.getProfiles();
    const updatedProfiles = profiles.map(p => 
      p.id === profileId 
        ? { ...p, ...updates, updatedAt: serverTimestamp() }
        : p
    );

    await this.saveProfiles(updatedProfiles);
  }

  /**
   * Delete a layout profile
   */
  async deleteProfile(profileId: string): Promise<void> {
    const profiles = await this.getProfiles();
    const filteredProfiles = profiles.filter(p => p.id !== profileId);
    
    // Ensure at least one default profile exists
    if (!filteredProfiles.some(p => p.isDefault)) {
      const firstProfile = filteredProfiles[0];
      if (firstProfile) {
        firstProfile.isDefault = true;
      } else {
        filteredProfiles.push(defaultLayoutProfile);
      }
    }

    await this.saveProfiles(filteredProfiles);
  }

  /**
   * Set a profile as the default
   */
  async setDefaultProfile(profileId: string): Promise<void> {
    const profiles = await this.getProfiles();
    const updatedProfiles = profiles.map(p => ({
      ...p,
      isDefault: p.id === profileId,
      updatedAt: serverTimestamp(),
    }));

    await this.saveProfiles(updatedProfiles);
  }

  /**
   * Reorder profiles (affects matching priority)
   */
  async reorderProfiles(profileIds: string[]): Promise<void> {
    const profiles = await this.getProfiles();
    const profileMap = new Map(profiles.map(p => [p.id, p]));
    
    const reorderedProfiles = profileIds
      .map((id, index) => {
        const profile = profileMap.get(id);
        if (profile) {
          return { ...profile, order: index, updatedAt: serverTimestamp() };
        }
        return null;
      })
      .filter((p): p is LayoutProfile => p !== null);

    await this.saveProfiles(reorderedProfiles);
  }
}

export const layoutProfileService = new LayoutProfileService();
export { defaultLayoutProfile };

